import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { IrisDmsFolderInfo, IrisDmsFolderInfoI } from '../models/IrisDmsFolderInfo';
import { setError, setLoaded, setLoading, withRequestState } from '@iris/common/signals/features/request-status.feature';
import { computed, inject } from '@angular/core';
import { IrisFoldersService } from '@iris/common/services/docs/folders.service';
import { Observable, debounceTime, filter, forkJoin, map, of, pipe, switchMap, take } from 'rxjs';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { IrisProjectsService } from '@iris/common/services/projects.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { IrisDMSFolder, IrisDMSFolderI } from '../models/IrisDMSFolder';

export const IrisDmsFolderInfoStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withState<IrisDMSFolderI>(new IrisDMSFolder()),
  withState<IrisDmsFolderInfoI>(new IrisDmsFolderInfo()),
  withRequestState(),
  withComputed(store => ({
    folderInfo: computed<Partial<IrisDMSFolderI & IrisDmsFolderInfoI>>(() => ({
      id: store.id(),
      name: store.name(),
      comment: store.comment(),
      customIconClass: store.customIconClass(),
      color: store.color(),
    })),
  })),
  withMethods((
    store,
    foldersService = inject(IrisFoldersService),
    globalSandbox = inject(IrisGlobalSandbox),
    projectsService = inject(IrisProjectsService),
  ) => ({
    fetchFolderInfo: rxMethod<string>(
      pipe(
        debounceTime(300),
        filter(() => !store.loading()),
        switchMap(folderId => {
          patchState(store, {}, setLoading());
          return forkJoin([
            foldersService.getFolderById(folderId),
            foldersService.getFolderInfo(folderId),
          ]).pipe(
            tapResponse({
              next: ([folder, folderInfo]) => patchState(store, folder, folderInfo, setLoaded()),
              error: (err: HttpErrorResponse) => patchState(store, setError(err.message)),
            }),
          );
        }),
      ),
    ),
    getProjectName$(projectId: number): Observable<string> {
      if (!projectId) {
        return of('-');
      }
      return globalSandbox.currentProject$.pipe(
        switchMap(currentProject => currentProject?.id === projectId ? of(currentProject) : projectsService.getProjectById(projectId).pipe(take(1))),
        map(project => project?.name || '-'));
    },
    updateFolderInfo(folder: Partial<IrisDmsFolderInfoI>): void {
      patchState(store, { ...folder });
    },
  })),
);
